import { useTranslation } from "react-i18next"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import clsx from "clsx"
import _ from "lodash"

import { Link } from "src/navigation";
import { Family } from "src/patents/patents"
import { IconChevronDown, IconChevronUp } from "src/components/icons"
import { familyUrl, isActiveMember, memberUrl } from "src/patents/utils"
import { FamilyGridDetails } from "../dennemeyer/components";
import { usePatents } from "src/patents/PatentsProvider";
import { useRenewalsFilter } from "./filter";
import { useClaimScopes } from "src/products/products";
import { useEffect, useState } from "react";
import { PlainImage } from "src/components/Image";
import { noHtml } from "src/utils/strings";


export function TableCard({children, indent = false}) {
    return (
        <div className={clsx(
            "grid grid-cols-subgrid col-span-4 gap-x-6 py-3 px-2 bg-white rounded-xl shadow-lg border border-pcx-200",
            indent && "ml-6",
        )}>
            {children}
        </div>
    )
}

export function FamilyHeader({family}: {family: Family}) {
    const {showDetailsFor, setShowDetailsFor} = useRenewalsFilter()
    const showDetails = showDetailsFor(family.patentFamilyId)

    return <>
        <h3 className="col-span-4 px-2 pb-2 max-w-full truncate flex flex-row justify-between gap-2">
            <Link className="min-w-0 underline-link text-ellipsis overflow-hidden" to={familyUrl(family)}>{family.internalReference}: {family.familyName}</Link>
            <button className="shrink-0 text-pcx-500" onClick={() => setShowDetailsFor(family.patentFamilyId, !showDetails)} >{showDetails
                ? <IconChevronUp />
                : <IconChevronDown />
            }</button>
        </h3>
        {showDetails && <div className="px-2 col-span-4 grid grid-cols-subgrid pt-1">
            <FamilyGridDetails family={family} />
        </div>}
    </>
}

const countryStyle = "after:content-[','] mr-1 last:mr-0 last:after:content-none text-pcx-700"

export function FamilyFooter({family, inDetail}: {family: Family, inDetail: number[]}) {
    const {t} = useTranslation()

    const {showDetailsFor} = useRenewalsFilter()
    const showDetails = showDetailsFor(family.patentFamilyId)
    const {membersByFamilyId} = usePatents()
    const missing = (membersByFamilyId[family.patentFamilyId] ?? []).filter(m => !inDetail.includes(m.familyMemberId) && isActiveMember(m))

    if (missing.length === 0 || !showDetails) return null

    const byCountry = _(missing).groupBy(m => m.countryCode).toPairs().sortBy(0).value()

    return <>
        <div className="col-span-4 pt-2 mt-1.5 px-2 first:pt-0 first:mt-0 border-t-2 first:border-t-0 border-pcx-200 text-slate-600" >
            {t('other-countries')}: {byCountry.map(([country, members]) =>
                members.length === 1 
                    ? <span key={country} className={countryStyle}><Link to={memberUrl(members[0])} className="underline-link">{country}</Link></span>
                    : <Popover key={country} className={clsx(countryStyle, "relative inline")}>
                        <PopoverButton className="underline-link inline px-0">{country}</PopoverButton>
                        <PopoverPanel className="absolute mt-1 left-0 z-10 bg-white shadow-lg p-4 space-y-1 rounded-xl border-2 border-pcx-200">
                            {members.map(member => <Link key={member.familyMemberId} to={memberUrl(member)} className="block whitespace-nowrap underline-link">{member.internalReference}</Link>)}
                        </PopoverPanel>
                    </Popover>
            )}
        </div>
    </>
}

// TODO: Claim scopes schöner
export function ClaimScopeHeader({claimScopeIds, family, claimScopeGroupIndex = undefined}: {claimScopeIds: number[], family: Family, claimScopeGroupIndex?: number}) {
    const {t} = useTranslation()
    const {claimscopeById, membersByClaimScopeId} = useClaimScopes()

    const {smallView} = useRenewalsFilter()
    const [showDetails, setShowDetails] = useState(!smallView)
    useEffect(() => setShowDetails(!smallView), [smallView])

    if (claimScopeIds === undefined || claimScopeIds.length === 0) 
        return null

    return (
        <div className="col-span-4 px-2">
            <h4 className="text-pcx-600 hover:text-pcx-500 flex flex-row justify-between gap-2">
                <Link to={familyUrl(family)}>{family.internalReference} - {t('claimScope')} {claimScopeGroupIndex + 1}</Link>
                <button className="shrink-0 text-pcx-500" onClick={() => setShowDetails(d => !d)} >{showDetails
                    ? <IconChevronUp />
                    : <IconChevronDown />
                }</button>
            </h4>

            {showDetails && <div className="flex flex-row gap-4 max-w-[80rem] overflow-x-auto">
                {_(claimScopeIds)
                .map(claimScopeId => {
                    const members = membersByClaimScopeId[claimScopeId] ?? []
                    const claimNumber = members[0]?.claim?.claimNumber ?? ''
                    const claimScope = claimscopeById[claimScopeId]
                    return {claimScopeId, members, claimNumber, claimScope}
                })
                .filter(({members, claimScope}) => members.length > 0 && claimScope !== undefined)
                .sortBy('claimNumber')
                .map(({claimScopeId, claimNumber, claimScope}) =>  {
                    return (
                        <div key={claimScopeId} className="flex flex-row gap-2 md:gap-4 items-start" title={noHtml(claimScope.claimScopeSummary)}>
                            <div className="w-44 h-32 p-1">
                                <PlainImage entity={"claim-scope"} entityId={claimScopeId} title={undefined} text={noHtml(claimScope.claimScopeSummary)} />
                            </div>
                            <div className="w-80 text-slate-500 text-sm pt-1">
                                <h5 className="pb-1 text-slate-600 text-base">{t('claim-noun')} {claimNumber}</h5>
                                <div className="text-xs" dangerouslySetInnerHTML={{ __html: claimScope.claimScopeSummary }}></div>
                            </div>
                        </div>
                    )
                }).value()}
            </div>}
        </div>
    )
}